import { KeyboardEvent, MouseEvent, memo, useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Icon } from '@/core/components/Icons';
import { useAppDispatch } from '@/core/store/store';
import { openFilter } from '@/core/store/reducers/filtersSlice';

import { openFocusModal, selectFocus } from '@/features/Focus/store';
import { setAnalysisViewModalOpen, setSelectedAnalysis } from '@/features/ChatBot/store';

import { DragHandle, FocusItemName, StyledFocusItem, StyledIconButton } from './FocusItem.styles';
import { FocusItemProps } from './FocusItem.types';

const FocusItemComponent = ({
  id,
  name,
  isSelected,
  shouldHideEditButton = false,
  useNormalCase = false,
  isDraggable = false,
  onClick,
}: FocusItemProps) => {
  const dispatch = useAppDispatch();

  const sortableId = String(id ?? `focus-item-${name}`);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: !isDraggable,
  });

  const dragStyle = useMemo(
    () => ({
      transform: CSS.Translate.toString(transform),
      transition,
      zIndex: isDragging ? 2 : 1,
      opacity: isDragging ? 0.85 : 1,
    }),
    [transform, transition, isDragging]
  );

  const handleEditFocusClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      dispatch(openFilter(null));
      dispatch(setAnalysisViewModalOpen(false));
      dispatch(setSelectedAnalysis(null));

      if (id != null) {
        dispatch(selectFocus(id));
      }
      dispatch(openFocusModal('edit'));
    },
    [dispatch, id]
  );

  const onKeyDownHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick]
  );

  const handleDragHandleClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <StyledFocusItem
      {...(isDraggable
        ? {
            ref: setNodeRef,
            style: dragStyle,
            ...attributes,
          }
        : {})}
      role="button"
      tabIndex={0}
      isSelected={isSelected}
      $normalCase={useNormalCase}
      onClick={onClick}
      onKeyDown={onKeyDownHandler}
    >
      {isDraggable && (
        <DragHandle
          onClick={handleDragHandleClick}
          aria-label="Drag to reorder"
          {...listeners}
        >
          <Icon
            name="GripDotsVertical"
            size={16}
          />
        </DragHandle>
      )}
      <FocusItemName>{name}</FocusItemName>
      {!shouldHideEditButton && (
        <StyledIconButton onClick={handleEditFocusClick}>
          <Icon name="Gear" />
        </StyledIconButton>
      )}
    </StyledFocusItem>
  );
};

export const FocusItem = memo(FocusItemComponent);
